import React, { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  addService,
  couponRedemptionLoading,
  editService,
  removeService,
  setTotalPrice,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { formatDate } from "../../../util/date";
import moment from "moment";
import { useFetchVoucher } from "../../../redux/flows/fetchVoucherFlow";
import { formatCurrency } from "../../../util";
import CustomButton from "../common/util/CustomButton";

interface BookingSummaryProps {
  roomImage: string;
  roomTitle: string;
  address: string;
  pricePerNight: number;
  dates: {
    start: string;
    end: string;
  };
  numberOfGuests: number;
  additionalServices: {
    id: string;
    assetId: string;
    serviceName: string;
    unitPrice: number;
    isPaymentCompulsory: boolean;
    quantity: boolean;
  }[];
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  roomImage,
  roomTitle,
  address,
  pricePerNight,
  dates,
  numberOfGuests,
  additionalServices,
}) => {
  const [showAll, setShowAll] = React.useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>(
    additionalServices.reduce<Record<string, number>>((acc, service) => {
      acc[service.serviceName] = 1;
      return acc;
    }, {})
  );
  const fetchVoucher = useFetchVoucher();

  const sortedAdditionalServices = [...additionalServices].sort((a, b) =>
    a.isPaymentCompulsory === b.isPaymentCompulsory ? 0 : a.isPaymentCompulsory ? 1 : -1
  );

  const displayedServices = showAll ? additionalServices : sortedAdditionalServices.slice(0, 4);

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const startDate = useSelector((state: RootState) => state.bookingCreation.orderData.checkInDate);
  const endDate = useSelector((state: RootState) => state.bookingCreation.orderData.checkOutDate);
  const isCouponReedemptionLoading = useSelector(
    (state: RootState) => state.bookingCreation.loading.couponRedemption
  );
  const locationId = useSelector(
    (state: RootState) => state.apartment.selectedApartment?.locationId
  );
  const groupId = useSelector((state: RootState) => state.apartment.selectedApartment?.groupId);
  const voucher = useSelector((state: RootState) => state.bookingCreation.roomRentalVoucher);

  const [voucherCode, setVoucherCode] = useState("");

  const [checkedServices, setCheckedServices] = useState<Record<string, boolean>>(
    displayedServices.reduce<Record<string, boolean>>((acc, service) => {
      acc[service.serviceName] = false;
      return acc;
    }, {})
  );

  const handleCheckboxChange = (serviceName: string) => {
    setCheckedServices((prevState) => {
      const newCheckedState = {
        ...prevState,
        [serviceName]: !prevState[serviceName],
      };

      if (!prevState[serviceName]) {
        dispatch(
          addService({
            serviceId: additionalServices.find((service) => service.serviceName === serviceName)
              ?.id!,
            name: serviceName,
            quantity: quantities[serviceName],
          })
        );
      } else {
        dispatch(removeService({ name: serviceName }));
      }

      return newCheckedState;
    });
  };

  const handleIncrement = (serviceName: string) => {
    setQuantities((prevQuantities) => {
      const newQuantities = {
        ...prevQuantities,
        [serviceName]: prevQuantities[serviceName] + 1,
      };
      dispatch(
        editService({
          serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
          name: serviceName,
          quantity: newQuantities[serviceName],
        })
      );
      return newQuantities;
    });
  };

  const handleDecrement = (serviceName: string) => {
    setQuantities((prevQuantities) => {
      const newQuantities = {
        ...prevQuantities,
        [serviceName]: Math.max(1, prevQuantities[serviceName] - 1),
      };
      dispatch(
        editService({
          serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
          name: serviceName,
          quantity: newQuantities[serviceName],
        })
      );
      return newQuantities;
    });
  };

  const additionalServicesTotal = additionalServices.reduce((total, service) => {
    if (checkedServices[service.serviceName] && !(service.unitPrice === 0) && service.unitPrice) {
      const quantity = service.quantity ? quantities[service.serviceName] : 1;
      return total + service.unitPrice * quantity;
    }
    return total;
  }, 0);

  const calculateNights = () => {
    if (!startDate || !endDate) {
      return 0;
    }
    return moment(endDate).diff(moment(startDate), "days");
  };

  const handleVoucherApply = async () => {
    dispatch(couponRedemptionLoading({ loading: true }));
    await fetchVoucher(voucherCode, locationId!, groupId!); // Todo: refactor to not use !
  };
  // Basic coupon validation - alphanumeric with optional dashes/underscores
  const isValidVoucher = (code: string) => {
    return /^[a-zA-Z0-9-_ ]+$/.test(code) && code.trim().length > 0;
  };
  const isApplyButtonDisabled = !isValidVoucher(voucherCode);

  const numberOfNights = calculateNights();

  let totalPrice = numberOfNights * pricePerNight + 50 + additionalServicesTotal;
  let reductionPrice = 0;
  if (voucher) {
    if (voucher.discountIsPercentage === true) {
      reductionPrice = totalPrice * (voucher.discountValue / 100);
    } else {
      reductionPrice = voucher.discountValue;
    }
  }
  totalPrice = totalPrice - reductionPrice;

  useEffect(() => {
    dispatch(setTotalPrice(totalPrice + reductionPrice));
  }, [dispatch, reductionPrice, totalPrice]);

  return (
    <div className="text-foreground max-w-[600px] mx-auto p-4 md:p-6">
      <div className="flex gap-4 mb-4 border-b border-foreground/10 pb-2">
        <img src={roomImage} alt="Room" className="w-[100px] h-[100px] object-cover rounded-lg" />
        <div className="flex-1 flex flex-col gap-1">
          <p className="font-medium text-base lg:text-[19px]">{roomTitle}</p>
          <p className="font-normal text-sm lg:text-[19px]">{address}</p>
          <p className="text-sm lg:text-[19px]">
            {formatCurrency(pricePerNight, i18n.language)} / {t("apartment.night")}
          </p>
        </div>
      </div>

      <div className="p-2 mb-1 border border-foreground/10 rounded-md">
        <p className="font-semibold text-sm lg:text-base">
          {t("book_location.booking_summary.first_section_subtitle")}
        </p>
        <div className="flex justify-between lg:text-[19px]">
          <p>{t("book_location.booking_summary.dates")}</p>
          <p>{`${formatDate(startDate)} - ${formatDate(endDate)}`}</p>
        </div>

        <div className="flex justify-between">
          <p>{t("book_location.booking_summary.number_of_guests")}</p>
          <p>{numberOfGuests}</p>
        </div>
      </div>

      <div className="pt-2 border border-foreground/10 p-2">
        <div className="mb-2 text-sm font-semibold lg:text-base">
          {t("book_location.booking_summary.second_section_subtitle")}
        </div>

        <p className="mb-4">
          <span className="text-foreground/60">
            {t("book_location.booking_summary.services_description")}
          </span>
        </p>
        {displayedServices
          .filter((service) => !service.isPaymentCompulsory && service.serviceName !== "Internet")
          .map((service, index) => (
            <div key={index} className="flex items-start justify-between gap-4 py-2">
              <label className="flex items-center mr-[15px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={checkedServices[service.serviceName]}
                  onChange={() => handleCheckboxChange(service.serviceName)}
                  className="peer hidden"
                />
                <span className="w-5 h-5 flex items-center justify-center rounded-sm box-border transition-colors border border-foreground/10 peer-checked:bg-primary peer-checked:border-primary">
                  {checkedServices[service.serviceName] && (
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>

                <span className="ml-2 lg:text-[19px] leading-none">
                  {service.serviceName}
                  {service.unitPrice === 0 ? (
                    <span className="text-foreground/60">
                      {" "}
                      | {t("book_location.booking_summary.free")}
                    </span>
                  ) : (
                    service.unitPrice && (
                      <span className="text-foreground/60">
                        {` | ${formatCurrency(service.unitPrice, i18n.language)}${" "}${t(
                          "apartment.per_person"
                        )} `}
                      </span>
                    )
                  )}
                </span>
              </label>

              {/* Quantity controls */}
              {checkedServices[service.serviceName] && service.quantity === true && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleDecrement(service.serviceName)}
                    className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-300 text-sm"
                  >
                    -
                  </button>
                  <div className="px-3 py-0.5 border border-gray-300 rounded-full text-center text-sm">
                    {quantities[service.serviceName]}
                  </div>
                  <button
                    onClick={() => handleIncrement(service.serviceName)}
                    className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-300 text-sm"
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          ))}
        {additionalServices.length > 5 && (
          <div
            onClick={() => setShowAll(!showAll)}
            className="text-primary cursor-pointer flex items-center mt-1"
          >
            <>
              {showAll ? t("apartment.show_less") : t("apartment.show_more")}
              <KeyboardArrowDownIcon
                className={`transition-transform duration-300 ${
                  showAll ? "rotate-180" : "rotate-0"
                }`}
              />
            </>
          </div>
        )}
      </div>

      <div className="flex flex-row justify-between items-center gap-4 my-2">
        <input
          type="text"
          name="coupon-input"
          placeholder={t("general.enter_coupon_code")}
          onChange={(e) => setVoucherCode(e.target.value)}
          className=" px-3 h-10 py-2 w-full border text-foreground border-foreground/10 bg-background rounded-lg outline-none focus:border-primary"
        />
        <CustomButton
          onClick={handleVoucherApply}
          loading={isCouponReedemptionLoading}
          disabled={isApplyButtonDisabled}
          spinnerColorClass="border-onPrimary border-t-primary"
          className="mt-2 bg-primary text-background"
        >
          {t("orders.ticket_overview.button")}
        </CustomButton>
      </div>

      {/* Price Details Section */}
      <div className="p-2 mt-2 border border-foreground/10 rounded-md">
        <p className="mb-2 font-semibold text-sm lg:text-base">
          {t("book_location.booking_summary.price_details")}
        </p>
        <div className="space-y-2">
          <div className="flex justify-between">
            <p>{`${numberOfNights} ${t("apartment.night", {
              count: numberOfNights,
            })}`}</p>
            <p>{formatCurrency(numberOfNights * pricePerNight, i18n.language)}</p>
          </div>
          {additionalServices
            .filter((service) => service.isPaymentCompulsory)
            .map((service) => (
              <div key={service.id} className="flex justify-between">
                <p>{service.serviceName}</p>
                <p>{formatCurrency(service.unitPrice, i18n.language)}</p>
              </div>
            ))}
          <div className="flex justify-between">
            <p>{t("book_location.booking_summary.additional_services")}</p>
            <p>{formatCurrency(additionalServicesTotal, i18n.language)}</p>
          </div>

          {reductionPrice > 0 && (
            <div className="flex justify-between">
              <p>{t("book_location.booking_summary.reduction")}</p>
              <p>-{formatCurrency(reductionPrice, i18n.language)}</p>
            </div>
          )}

          <div className="flex justify-between pt-3 font-medium border-t border-foreground/10">
            <p>{t("book_location.booking_summary.total")}</p>
            <p>{formatCurrency(totalPrice, i18n.language)}</p>
          </div>
          <p className="text-sm text-foreground/80">
            {t("book_location.confirmation.tax_information")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
